import { Decimal } from '@snap-types/types/decimal';
import { z } from 'zod';

import { WritableQuoteBuilderStateType } from '../../types/state';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';
import { parseNumberInput, validateDecimalPrecision } from './number-input';

type ValidateUnitCostParams = {
  unitCost: string | Decimal;
  quoteLineUuid: string;
  quotePriceUuid: string;
};

const validator = z
  .number({
    error: 'Unit cost must be a number',
  })
  .positive({ message: 'Unit cost must be greater than 0' });

export function validateUnitCost(
  state: WritableQuoteBuilderStateType,
  { unitCost, quoteLineUuid, quotePriceUuid }: ValidateUnitCostParams,
) {
  const unitCostString = unitCost.toString();

  const invalidIssueId = buildIssueId({
    area: 'quotePrice',
    quoteLineUuid,
    quotePriceUuid,
    type: 'invalid',
    field: 'unitCost',
  });

  const parsed = parseNumberInput({
    value: unitCostString,
    requiredMessage: 'Unit cost is required',
    invalidMessage: 'Unit cost must be a number',
  });

  if (parsed.error) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid unit cost',
      description: parsed.error,
      severity: 'error',
      type: 'invalid',
      field: 'unitCost',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'unitCost',
        }),
      },
    });
  }

  const precisionError = validateDecimalPrecision({
    value: unitCostString,
    maxDecimalPlaces: 8,
    maxIntegerDigits: 15,
    decimalPlacesMessage: 'Unit cost cannot have more than 8 decimal places',
    integerDigitsMessage:
      'Unit cost cannot have more than 15 digits before the decimal point',
  });

  if (precisionError) {
    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid unit cost',
      description: precisionError,
      severity: 'error',
      type: 'invalid',
      field: 'unitCost',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'unitCost',
        }),
      },
    });
  }

  const result = validator.safeParse(parsed.parsed);

  if (!result.success) {
    const error = result.error.flatten().formErrors[0];

    return upsertIssue(state, invalidIssueId, {
      title: 'Invalid unit cost',
      description: error,
      severity: 'error',
      type: 'invalid',
      field: 'unitCost',
      area: 'quotePrice',
      quoteLineUuid,
      quotePriceUuid,
      action: {
        type: 'modify',
        elementId: buildElementId({
          uuid: quotePriceUuid,
          field: 'unitCost',
        }),
      },
    });
  }

  deleteIssue(state, invalidIssueId);

  return new Decimal(result.data);
}
